import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from '@/environments/environment';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { BasePricingModel } from '@/app/common/general-types/pricing.dto';
import { audienceFeesAt, estimateRankMultiplier, quotePieces, ratesFromPricingModel } from '@/app/common/brief-pieces/piece-price';
import { Piece } from '@/app/common/brief-pieces/piece-list';
import { CampaignService } from '@/app/services/campaign/campaign.service';
import { ImageService } from '@/app/services/image/image.service';
import { PricingModelV2Service } from '@/app/services/pricing-model-v2/pricing-model-v2.service';
import { CreateCampaignComponent } from '@/app/molecules/create-campaign/create-campaign.component';
import { AgencyPricing } from '@/app/pages/create-brief-basic/steps/agency-pricing/agency-pricing';
import { CreateBriefService } from '@/app/pages/briefs/service/create-brief/create-brief.service';
import { BriefBasicsCampaignStore } from '@/app/pages/create-brief-select/sections/brief-basics-section/brief-basics-campaign.store';
import {
  BRIEF_BASICS_BRAND_CONTEXT,
  BRIEF_BASICS_CAMPAIGN_API,
  BRIEF_BASICS_CAMPAIGN_HOST,
} from '@/app/pages/create-brief-select/sections/brief-basics-section/brief-basics-campaign.tokens';

/** Campaign, pricing model, and price per creator for a UGC Live brief. */
@Component({
  selector: 'cbss-ugc-create-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CreateCampaignComponent, AgencyPricing],
  providers: [
    BriefBasicsCampaignStore,
    { provide: BRIEF_BASICS_CAMPAIGN_HOST, useExisting: CreateBriefService },
    { provide: BRIEF_BASICS_CAMPAIGN_API, useExisting: CampaignService },
    { provide: BRIEF_BASICS_BRAND_CONTEXT, useExisting: CurrentUserService },
  ],
  templateUrl: './ugc-create-panel.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UgcCreatePanel implements OnInit, OnDestroy {
  readonly service = inject(CreateBriefService);
  readonly imageService = inject(ImageService);
  readonly campaignStore = inject(BriefBasicsCampaignStore);
  private readonly currentUser = inject(CurrentUserService);
  private readonly pricingModels = inject(PricingModelV2Service);
  private readonly subscriptionList = new Subscription();
  private defaultCampaignApplied = false;
  private campaignRestored = false;
  private pricingRestored = false;

  readonly savedCampaignId = input<string | null>(null);
  readonly savedPricingModelId = input<string | null>(null);

  readonly agencyModels = signal<BasePricingModel[]>([]);
  readonly creatingModel = signal(false);

  readonly pricePerCreatorLabel = computed(() => {
    const amounts = this.perCreatorAmounts();
    if (!amounts.length) {
      return '';
    }
    return this.moneyRange(Math.min(...amounts), Math.max(...amounts));
  });

  constructor() {
    effect(() => {
      const campaigns = this.campaignStore.allCampaigns();
      const savedId = this.savedCampaignId();
      if (!campaigns) {
        return;
      }
      if (savedId && !this.campaignRestored) {
        const match = campaigns.find((campaign) => campaign.id === savedId);
        if (match) {
          this.campaignStore.selectCampaign(match);
          this.defaultCampaignApplied = true;
          this.campaignRestored = true;
          return;
        }
      }
      if (this.service.selectedCampaign() || this.defaultCampaignApplied) {
        return;
      }
      this.defaultCampaignApplied = true;
      const fallback = campaigns.find((campaign) => campaign.campaignType === 1);
      if (fallback) {
        this.campaignStore.selectCampaign(fallback);
      }
    });
    effect(() => {
      const savedId = this.savedPricingModelId();
      if (!savedId || this.pricingRestored) {
        return;
      }
      const match = [...this.agencyModels(), ...this.service.pricingModelOptions()].find((model) => model.id === savedId);
      if (!match) {
        return;
      }
      this.service.selectedPricingModel.set(match);
      this.pricingRestored = true;
    });
  }

  ngOnInit(): void {
    this.campaignStore.loadCampaigns();
    if (this.shoutAgency()) {
      const agencyId = this.currentUser.focussedAgency?.agency.id;
      if (!agencyId) {
        return;
      }
      this.subscriptionList.add(
        this.pricingModels.getAgencyPricingModels(agencyId).subscribe({
          next: (models) => this.agencyModels.set(models),
        }),
      );
      return;
    }
    this.service.getPricingModelOptions();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  shoutAgency(): boolean {
    return this.currentUser.focussedAgency?.agency.id === environment.shoutAgencyId;
  }

  chooseModel(model: BasePricingModel | undefined): void {
    if (!model) {
      return;
    }
    this.service.selectedPricingModel.set(model);
    this.creatingModel.set(false);
  }

  modelClass(id: string): string {
    const selected = this.service.selectedPricingModel()?.id === id;
    return selected
      ? 'btn-shout btn-shout-xs btn-shout-primary'
      : 'btn-shout btn-shout-xs btn-shout-secondary';
  }

  private perCreatorAmounts(): number[] {
    const pieces = this.service.pieces() ?? [];
    const model = this.service.selectedPricingModel();
    if (!pieces.length || !model?.id) {
      return [];
    }
    const ugcOnly = pieces.every((piece) => piece.placements.every((placement) => placement.kind !== 'social'));
    const bonuses = ugcOnly ? [0] : (model.influencerLevels ?? []).map((level) => level.bonusValue);
    const levels = bonuses.length ? bonuses : [0];
    return levels.map((bonus) => this.quote(pieces, model, bonus));
  }

  private quote(pieces: Piece[], model: BasePricingModel, bonus: number): number {
    return quotePieces({
      pieces,
      rates: ratesFromPricingModel(model),
      rankMultiplier: estimateRankMultiplier(model.ranks),
      audienceFees: audienceFeesAt(pieces, bonus / 100),
      adLicenseSelected: this.service.selectedAdvertisingOnly(),
    }).net;
  }

  private money(value: number): string {
    const amount = value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${this.service.pricingModelCurrencySymbol()}${amount}`;
  }

  private moneyRange(min: number, max: number): string {
    return min === max ? this.money(min) : `${this.money(min)} – ${this.money(max)}`;
  }
}
